import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Button, DropdownButton } from '@bringg/react-components';
import { Run, User, Task } from '@bringg/types';
import { getRootEnv } from '@bringg-frontend/bringg-web-infra';

import { UserAssignmentModalParams, ViewNames } from '../consts';
import { useHasFeatureFlag } from 'bringg-web/utils/feature-flags';

import './user-assigner-footer.scss';

export interface UserAssignerFooterProps {
	selectedRun: Run;
	selectedDriver: User | null;
	modalParams: UserAssignmentModalParams;
	unstartedRunTasks: Task[];
	assignToRun: (run: Partial<Run>) => void;
	setError: (error: string) => void;
	setView: (viewName: ViewNames) => void;
	isCombinedOptimization: boolean;
	startOptimization: () => void;
}

export type UnassignButtonTypes = 'driverAndOrders' | 'orders' | 'driverOrDriverAndOrders';

const AssignButton: React.FC<UserAssignerFooterProps> = ({
	selectedRun,
	selectedDriver,
	modalParams,
	unstartedRunTasks,
	assignToRun,
	setError,
	setView,
	isCombinedOptimization,
	startOptimization
}: UserAssignerFooterProps) => {
	const { t } = useTranslation();

	const optimizeEnabled = useHasFeatureFlag('optimize_user_assigner_modal');

	const isRunStartedOrTasksAssign = useMemo(() => {
		if (!modalParams.data) {
			return false;
		}

		return (
			!modalParams.source.run ||
			(modalParams.source.run.started_at && unstartedRunTasks.length < unstartedRunTasks?.length)
		);
	}, [modalParams, unstartedRunTasks]);

	const assignToSameRun = () => {
		return assignToRun({ id: modalParams.source.run.id });
	};

	const goToRunCreationView = () => {
		setView('newRoute');
		setError('');
	};

	const sendToGoogleAnalytics = async (key: string) => {
		await getRootEnv().dashboardSdk.sdk.userAnalytics.trackEvent({
			key: `RM:${key}`
		});
	};

	const onOptimizationStart = async () => {
		await sendToGoogleAnalytics('RM:userAssignOptimization');
		startOptimization();
	};

	const getAssignRunButton = () => {
		if (selectedRun) {
			return (
				<Button
					key="create-run"
					type="primary"
					className={classNames('assign-run', 'assign-run-merge')}
					disabled={!selectedDriver && !selectedRun}
					onClick={() => assignToRun(selectedRun)}
				>
					{t('RUN_USER_ASSIGNMENT.MERGE_ROUTE')}
				</Button>
			);
		} else if (optimizeEnabled && isRunStartedOrTasksAssign && isCombinedOptimization) {
			return (
				<DropdownButton
					buttonText={t('RUN_USER_ASSIGNMENT.OPTIMIZE_ROUTE')}
					onButtonClick={onOptimizationStart}
					disabled={!selectedDriver && !selectedRun}
					mainButtonTestId="optimize-button"
					menuItems={[
						{
							key: 1,
							label: t('RUN_USER_ASSIGNMENT.NEW_ROUTE'),
							onClick: async () => {
								await sendToGoogleAnalytics('RM:userAssignManual');
								goToRunCreationView();
							},
							disabled: !selectedDriver && !selectedRun,
							className: 'assign-run-create-new'
						}
					]}
				/>
			);
		}

		const userAssignmentText = isRunStartedOrTasksAssign
			? 'RUN_USER_ASSIGNMENT.NEW_ROUTE'
			: 'RUN_USER_ASSIGNMENT.ASSIGN_DRIVER';
		const buttonClassName = isRunStartedOrTasksAssign ? 'assign-run-create-new' : 'assign-run-change-driver';
		const onClickAction = isRunStartedOrTasksAssign ? goToRunCreationView : assignToSameRun;
		return (
			<Button
				key="create-run"
				type="primary"
				className={classNames('assign-run', buttonClassName)}
				disabled={!selectedDriver && !selectedRun}
				onClick={onClickAction}
			>
				{t(userAssignmentText)}
			</Button>
		);
	};

	return <>{getAssignRunButton()}</>;
};

export default AssignButton;
